import { EventSource as ServerSentEventSource } from 'eventsource';

import { isDefined, isNotDefined, SECOND } from '@enterprise/core';

import type { CoordinationMessage } from '#context/sse/hooks/types/coordination-message.type';
import type { ReadinessListener } from '#context/sse/types/readiness-listener.type';
import type { SseRuntimeStore } from '#context/sse/types/sse-runtime-store.type';

import {
  CHANNEL_NAME,
  ELECTION_TIMEOUT_MS,
  FOLLOWER_CHECK_INTERVAL,
  HEARTBEAT_EVENT,
  HEARTBEAT_TIMEOUT,
  LEADER_PING_INTERVAL,
  LEADER_TIMEOUT,
  RESYNC_EVENT,
} from '#context/sse/common/constants';
import { ReconnectReason } from '#context/sse/types/reconnect-reason.enum';

import { calculateReconnectDelay } from './utilities/calculate-reconnect-delay';

/**
 * Creates a runtime store for managing Server-Sent Events (SSE) connections with multi-tab coordination.
 *
 * This store handles:
 * - Establishing and managing an SSE connection as a "leader" tab.
 * - Coordinating leadership and event propagation across browser tabs using BroadcastChannel.
 * - Reconnecting on errors or leadership changes.
 * - Dispatching local and global SSE events to registered listeners.
 * - Managing readiness state and notifying listeners of changes.
 *
 * @param uri - The SSE endpoint URI to connect to.
 * @param headers - A record of headers to include in the SSE connection request.
 * @param onGlobalMessage - Optional callback invoked for global SSE messages.
 * @returns An object implementing the {@link SseRuntimeStore} interface, providing methods and properties
 * for SSE management and tab coordination.
 */
export const createSseRuntimeStore = (
  uri: string,
  headers: Record<string, string | undefined>,
  onGlobalMessage?: (e: MessageEvent<string>) => void
) => {
  const channel = new BroadcastChannel(CHANNEL_NAME);
  const readinessListeners = new Set<ReadinessListener>();
  const tabId = crypto.randomUUID();

  let connecting = false;
  let denied = false;
  let followerWatchdogTimer: ReturnType<typeof setInterval> | null = null;
  let heartbeatWatcherTimer: ReturnType<typeof setInterval> | null = null;
  let lastHeartbeat = Date.now();
  let lastLeaderAlive = Date.now();
  let leaderHeartbeatTimer: ReturnType<typeof setInterval> | null = null;
  let leadershipResolved = false;

  const clearAllTimers = () => {
    if (isDefined(followerWatchdogTimer)) {
      clearInterval(followerWatchdogTimer);
      followerWatchdogTimer = null;
    }

    if (isDefined(leaderHeartbeatTimer)) {
      clearInterval(leaderHeartbeatTimer);
      leaderHeartbeatTimer = null;
    }

    if (isDefined(heartbeatWatcherTimer)) {
      clearInterval(heartbeatWatcherTimer);
      heartbeatWatcherTimer = null;
    }
  };

  const startHeartbeatWatcher = () => {
    // 🔴 NEW
    if (isDefined(heartbeatWatcherTimer)) {
      return;
    }

    heartbeatWatcherTimer = setInterval(() => {
      if (!store.isLeader || isNotDefined(store.eventSource)) {
        return;
      }

      if (Date.now() - lastHeartbeat > HEARTBEAT_TIMEOUT) {
        store.disconnect();
        store.scheduleReconnect(ReconnectReason.HeartbeatTimeout);
      }
    }, SECOND);
  };

  const startFollowerWatchdog = () => {
    if (isDefined(followerWatchdogTimer)) {
      return;
    }

    followerWatchdogTimer = setInterval(() => {
      if (store.isLeader) {
        return;
      }

      const elapsed = Date.now() - lastLeaderAlive;

      if (elapsed > LEADER_TIMEOUT) {
        const electionId = crypto.randomUUID();

        denied = false;
        leadershipResolved = false;
        lastLeaderAlive = Date.now();

        channel.postMessage({ electionId, tabId, type: 'claim' });

        setTimeout(() => {
          if (!denied && !leadershipResolved) {
            leadershipResolved = true;
            store.setReadiness({ isLeader: true });
            store.connect(ReconnectReason.LeadershipGained);
          }
        }, ELECTION_TIMEOUT_MS);
      }
    }, FOLLOWER_CHECK_INTERVAL);
  };

  const startLeaderHeartbeat = () => {
    if (isDefined(leaderHeartbeatTimer)) {
      return;
    }

    leaderHeartbeatTimer = setInterval(() => {
      if (!store.isLeader) {
        return;
      }

      channel.postMessage({ tabId, type: 'leader-alive' });
    }, LEADER_PING_INTERVAL);
  };

  const store: SseRuntimeStore = {
    addEventListener(eventName: string) {
      if (!store.isLeader || isNotDefined(store.eventSource)) {
        return;
      }

      store.eventSource.addEventListener(eventName, (e: MessageEvent<string>) => {
        channel.postMessage({
          data: e.data,
          event: eventName,
          type: 'event',
        });

        store.dispatchLocalEvent(eventName, e.data);
      });
    },
    channel,
    connect(_reason) {
      if (!store.isLeader || connecting || isDefined(store.eventSource)) {
        return;
      }

      connecting = true;

      const validHeaders = Object.fromEntries(
        Object.entries(headers).filter(([, value]) => {
          return isDefined(value);
        })
      ) as Record<string, string>;

      const sse = new ServerSentEventSource(uri, {
        fetch: (input, init) => {
          return fetch(input, {
            ...init,
            credentials: 'include',
            headers: validHeaders,
          });
        },
      });

      sse.onerror = () => {
        store.disconnect();
        store.scheduleReconnect(ReconnectReason.Error);
      };

      sse.onmessage = (e: MessageEvent<string>) => {
        onGlobalMessage?.(e);

        channel.postMessage({
          data: e.data,
          event: e.type || 'message',
          type: 'global-event',
        });
      };

      sse.onopen = () => {
        connecting = false;

        store.reconnectAttempts = 0;
        store.setReadiness({ readyState: EventSource.OPEN });

        startLeaderHeartbeat();
        startHeartbeatWatcher();

        channel.postMessage({ tabId, type: 'leader-alive' });
      };

      sse.addEventListener(HEARTBEAT_EVENT, () => {
        lastHeartbeat = Date.now();
      });

      for (const eventName of Object.keys(store.listeners)) {
        sse.addEventListener(eventName, (e: MessageEvent<string>) => {
          channel.postMessage({
            data: e.data,
            event: eventName,
            type: 'event',
          });

          store.dispatchLocalEvent(eventName, e.data);
        });
      }

      store.eventSource = sse;
    },
    disconnect() {
      store.eventSource?.close();
      store.eventSource = null;
      connecting = false;

      if (isDefined(store.reconnectTimer)) {
        clearTimeout(store.reconnectTimer);
        store.reconnectTimer = null;
      }

      store.setReadiness({ readyState: EventSource.CONNECTING });

      clearAllTimers();
    },
    dispatchLocalEvent: (event, data) => {
      const handlers = store.listeners[event];

      if (isNotDefined(handlers)) {
        return;
      }

      const message = new MessageEvent(event, { data });

      for (const handler of handlers) {
        handler(message);
      }
    },
    eventSource: null,
    isLeader: false,
    lastHeartbeat,
    listeners: {},
    readyState: EventSource.CONNECTING,
    reconnectAttempts: 0,
    reconnectTimer: null,
    scheduleReconnect(reason: ReconnectReason) {
      if (!store.isLeader) {
        return;
      }

      if (isDefined(store.reconnectTimer)) {
        clearTimeout(store.reconnectTimer);
      }

      const delay = calculateReconnectDelay(store.reconnectAttempts);

      store.reconnectTimer = setTimeout(() => {
        store.reconnectTimer = null;

        if (!store.isLeader || store.eventSource) {
          return;
        }

        store.reconnectAttempts++;
        store.connect(reason);
      }, delay);
    },
    setReadiness: ({ isLeader, readyState }) => {
      let changed = false;

      if (isDefined(readyState) && readyState !== store.readyState) {
        store.readyState = readyState;
        changed = true;
      }

      if (isDefined(isLeader) && isLeader !== store.isLeader) {
        store.isLeader = isLeader;
        changed = true;

        if (isLeader) {
          startLeaderHeartbeat();
          startHeartbeatWatcher();
        } else {
          clearAllTimers();
          startFollowerWatchdog();
        }
      }

      if (changed) {
        const snapshot = {
          isLeader: store.isLeader,
          readyState: store.readyState,
        };

        for (const listener of readinessListeners) {
          listener(snapshot);
        }
      }
    },
    subscribeReadiness(listener) {
      readinessListeners.add(listener);

      listener({ isLeader: store.isLeader, readyState: store.readyState });

      return () => {
        readinessListeners.delete(listener);
      };
    },
    tabId,
  };

  channel.onmessage = ({ data }: MessageEvent<CoordinationMessage>) => {
    switch (data.type) {
      case 'claim':
        if (store.isLeader && data.tabId !== tabId) {
          channel.postMessage({ tabId: data.tabId, type: 'deny' });
        }
        break;

      case 'deny':
        if (data.tabId === tabId) {
          denied = true;
          store.setReadiness({ isLeader: false });
        }
        break;

      case 'event':
        store.dispatchLocalEvent(data.event, data.data);
        break;

      case 'global-event':
        onGlobalMessage?.(new MessageEvent(data.event, { data: data.data }));
        break;

      case 'leader-alive':
        if (data.tabId !== tabId) {
          lastLeaderAlive = Date.now();
        }
        break;

      case 'resync':
        store.dispatchLocalEvent(RESYNC_EVENT, '');
        break;
    }
  };

  channel.postMessage({ tabId, type: 'claim' });

  setTimeout(() => {
    if (leadershipResolved) {
      return;
    }

    leadershipResolved = true;

    if (!denied) {
      store.setReadiness({ isLeader: true });
      store.connect(ReconnectReason.LeadershipGained);
    }
  }, ELECTION_TIMEOUT_MS);

  startFollowerWatchdog();

  return store;
};
